import React, { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Columns3, FileSpreadsheet, X, Loader2,
} from "lucide-react";

/**
 * Reusable ERP data table.
 * Props:
 *  columns: [{ key, label, sortable, render(row), visibleByDefault, width, className }]
 *  fetchData: async ({ skip, limit, sort, search, filters }) => { items, total }
 *  onExport: async () => triggers server export (returns blob)
 *  filters: [{ key, label, options: [{value, label}] }]
 *  rowKey: string (default 'id')
 *  onRowClick(row)
 *  storageKey: string for saving column visibility to localStorage
 *  headerAction: React node rendered top-right
 */
export default function DataTable({
  columns,
  fetchData,
  onExport,
  filters = [],
  rowKey = "id",
  onRowClick,
  storageKey,
  headerAction,
  emptyMessage = "No records found.",
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState(`-${columns.find((c) => c.sortable)?.key || "created_at"}`);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [exporting, setExporting] = useState(false);

  const [visibleCols, setVisibleCols] = useState(() => {
    if (storageKey) {
      try {
        const saved = JSON.parse(localStorage.getItem(`dt:${storageKey}`) || "null");
        if (saved) return saved;
      } catch (_) { /* ignore */ }
    }
    return columns.reduce((acc, c) => ({ ...acc, [c.key]: c.visibleByDefault !== false }), {});
  });

  const persistCols = (v) => {
    setVisibleCols(v);
    if (storageKey) localStorage.setItem(`dt:${storageKey}`, JSON.stringify(v));
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData({ skip, limit, sort, search, filters: activeFilters });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [fetchData, skip, limit, sort, search, activeFilters]);

  React.useEffect(() => { load(); }, [load]);

  // Debounce search
  const [searchDraft, setSearchDraft] = useState("");
  React.useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchDraft);
      setSkip(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchDraft]);

  function toggleSort(key) {
    if (sort === key) setSort(`-${key}`);
    else if (sort === `-${key}`) setSort(key);
    else setSort(`-${key}`);
  }

  async function handleExport() {
    if (!onExport) return;
    setExporting(true);
    try {
      await onExport();
    } finally {
      setExporting(false);
    }
  }

  const hasActiveFilters = Object.values(activeFilters).some((v) => v);
  const visibleColumns = columns.filter((c) => visibleCols[c.key]);
  const currentPage = Math.floor(skip / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            data-testid="dt-search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search..."
            className="pl-8 h-9"
          />
        </div>
        {filters.map((f) => (
          <div key={f.key} className="min-w-[140px]">
            <Select
              value={activeFilters[f.key] || "all"}
              onValueChange={(v) => { setActiveFilters({ ...activeFilters, [f.key]: v === "all" ? "" : v }); setSkip(0); }}
            >
              <SelectTrigger data-testid={`dt-filter-${f.key}`} className="h-9">
                <SelectValue placeholder={f.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All {f.label}</SelectItem>
                {(f.options || []).map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={() => { setActiveFilters({}); setSkip(0); }} className="h-9 text-xs" data-testid="dt-clear-filters">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
        <div className="flex-1" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9" data-testid="dt-column-select">
              <Columns3 className="w-3.5 h-3.5 mr-1" /> Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 max-h-96 overflow-y-auto">
            <DropdownMenuLabel>Show columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={visibleCols[c.key]}
                onCheckedChange={(v) => persistCols({ ...visibleCols, [c.key]: !!v })}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {onExport && (
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="h-9" data-testid="dt-export">
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />}
            Excel
          </Button>
        )}
        {headerAction}
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {visibleColumns.map((c) => (
                  <th
                    key={c.key}
                    className={`px-4 py-3 font-semibold text-slate-700 text-[11px] uppercase tracking-wider whitespace-nowrap ${c.sortable ? "cursor-pointer hover:bg-slate-100" : ""}`}
                    onClick={() => c.sortable && toggleSort(c.key)}
                    style={c.width ? { width: c.width } : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.sortable && sort === c.key && <ChevronUp className="w-3 h-3" />}
                      {c.sortable && sort === `-${c.key}` && <ChevronDown className="w-3 h-3" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={visibleColumns.length} className="text-center py-16">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-900 inline" />
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={visibleColumns.length} className="text-center py-16 text-slate-400 text-sm">
                  {emptyMessage}
                </td></tr>
              ) : (
                items.map((row) => (
                  <tr
                    key={row[rowKey]}
                    data-testid={`dt-row-${row[rowKey]}`}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={onRowClick ? "hover:bg-slate-50 cursor-pointer transition-colors" : ""}
                  >
                    {visibleColumns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 ${c.className || ""}`}>
                        {c.render ? c.render(row) : (row[c.key] ?? <span className="text-slate-300">—</span>)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50/50">
          <div className="text-xs text-slate-500">
            {loading ? "Loading..." : `${skip + 1}-${Math.min(skip + limit, total)} of ${total}`}
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(limit)} onValueChange={(v) => { setLimit(parseInt(v)); setSkip(0); }}>
              <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="dt-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              data-testid="dt-prev-page"
              title={skip === 0 ? "You're already on the first page" : "Previous page"}
              disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs font-mono text-slate-700 min-w-[60px] text-center">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              data-testid="dt-next-page"
              title={skip + limit >= total ? "You're already on the last page" : "Next page"}
              disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to trigger a file download from a fetch response
export async function downloadBlob(response, filename) {
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
